import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Icon } from '@cotera/client/app/components/ui/icon';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import {
  useIsFetching,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import {
  AddCreditsSection,
  AutoTopUpSection,
  CreditCardSettings,
} from './credit-card';
import { SubscriptionSettings } from './subscription-settings';
import { Assert } from '@cotera/utilities';
import { usePlan } from '../settings-views';
import { formatCredits } from '../../components/topbar/credit-balance-section';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';

// Plan section component
const PlanSection: React.FC = () => {
  const plan = usePlan();

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-medium">Plan</h3>
      <div className="flex items-center gap-3 rounded-lg border border-border bg-white p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <Icon icon="sparkles" className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Current Plan
          </p>
          <p className="text-lg font-semibold">{plan.billingPlanName}</p>
        </div>
      </div>
    </section>
  );
};

const RefreshButton: React.FC = () => {
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const billingQueryKey = queryKeys.settings.billing.all(orgId);
  const isFetching = useIsFetching({ queryKey: billingQueryKey });

  return (
    <Button
      size="icon-sm"
      variant="outline"
      title="Refresh"
      onClick={() => {
        void queryClient.invalidateQueries({ queryKey: billingQueryKey });
        void queryClient.invalidateQueries({ queryKey: [orgId, 'plan'] });
      }}
    >
      <Icon
        icon="arrow-path"
        className={`h-3.5 w-3.5 ${isFetching > 0 ? 'animate-spin' : ''}`}
      />
    </Button>
  );
};

export const BillingSettingsView: React.FC = () => {
  const { data: billingSettingsInfoRes } = useBillingSettingsInfo();

  if (billingSettingsInfoRes.isErr()) {
    return (
      <div className="space-y-8">
        <PlanSection />
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Billing Details</h3>
            <RefreshButton />
          </div>
          <p className="text-sm text-muted-foreground">
            {billingSettingsInfoRes.error.err.errorType}
          </p>
        </section>
      </div>
    );
  }

  const { billingSettingsInfo } = billingSettingsInfoRes.value;

  if (billingSettingsInfo.kind === 'legacy-enterprise') {
    return (
      <div className="space-y-8">
        <PlanSection />
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Billing Details</h3>
            <RefreshButton />
          </div>
          <p
            className="text-sm text-muted-foreground"
            data-billing-kind={billingSettingsInfo.kind}
          >
            Enterprise billing is managed separately. Contact support for
            details.
          </p>
        </section>
      </div>
    );
  }

  if (billingSettingsInfo.kind === 'orb-enterprise') {
    return (
      <div className="space-y-8">
        <PlanSection />

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Billing Details</h3>
            <div className="flex items-center gap-2">
              <RefreshButton />
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  window.open(
                    billingSettingsInfo.orbEnterpriseInfo.invoicesPortalUrl,
                    '_blank'
                  );
                }}
              >
                <Icon
                  icon="arrow-top-right-on-square"
                  className="h-3.5 w-3.5"
                />
                View Invoices
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-white p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Credit Balance
              </p>
              <p className="mt-1 text-2xl font-semibold">
                {formatCredits(
                  billingSettingsInfo.orbEnterpriseInfo.balance.credits
                )}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-white p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Engineering Hours
              </p>
              <p className="mt-1 text-2xl font-semibold">
                {billingSettingsInfo.orbEnterpriseInfo.balance.engineeringHours}
              </p>
            </div>
          </div>

          {billingSettingsInfo.orbSubscriptionHistory.length > 0 && (
            <div className="space-y-2">
              <Label>Subscription History</Label>
              <SubscriptionSettings
                subscriptions={billingSettingsInfo.orbSubscriptionHistory}
              />
            </div>
          )}
        </section>
      </div>
    );
  }

  const { orbInfo } = billingSettingsInfo;

  const subscriptions =
    billingSettingsInfo.kind === 'orb-business'
      ? billingSettingsInfo.orbSubscriptionHistory
      : null;

  const disableAddCredits: boolean =
    orbInfo.creditCardInfo.kind === 'saved-card'
      ? false
      : orbInfo.creditCardInfo.kind === 'no-saved-card'
      ? true
      : Assert.unreachable(orbInfo.creditCardInfo);

  return (
    <div className="space-y-8">
      <PlanSection />

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Billing Details</h3>
          <div className="flex items-center gap-2">
            <RefreshButton />
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                window.open(orbInfo.invoicesPortalUrl, '_blank');
              }}
            >
              <Icon icon="arrow-top-right-on-square" className="h-3.5 w-3.5" />
              View Invoices
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Credit Balance
          </p>
          <p className="mt-1 text-2xl font-semibold">
            ${orbInfo.creditsBalance.toFixed(2)}
          </p>
        </div>

        <div className="space-y-2">
          <Label>Payment Method</Label>
          <CreditCardSettings creditCardInfo={orbInfo.creditCardInfo} />
          <AddCreditsSection className="mt-4" disabled={disableAddCredits} />
          <AutoTopUpSection
            className="mt-6"
            currentTopUp={orbInfo.topUpConfig}
          />
        </div>

        {subscriptions && (
          <div className="space-y-2">
            <Label>Subscription History</Label>
            <SubscriptionSettings subscriptions={subscriptions} />
          </div>
        )}
      </section>
    </div>
  );
};

const useBillingSettingsInfo = () => {
  const client = useTenantedClient();
  const org = useWhoami((s) => s.org);

  return useSuspenseQuery({
    queryKey: queryKeys.settings.billing.billingSettingsInfo({ orgId: org.id }),
    queryFn: async () => {
      return await client.billingSettings.getBillingSettingsInfo({});
    },
  });
};
